import { Trans } from '@lingui/macro'
import { AutoColumn } from 'components/Column'
import { RowBetween } from 'components/Row'
import { isSupportedChain } from 'constants/chains'
import { groupPositionsByPool, useAllAdminPositions } from 'hooks/useAllAdminPositions'
import { useMemo } from 'react'
import { Inbox } from 'react-feather'
import styled, { css, useTheme } from 'styled-components/macro'
import { ThemedText } from 'theme'
import { gradientBorderSecondary } from 'theme/styles'
import { useAccount } from 'wagmi'

import { LoadingRows } from '../Pool/styleds'
import PoolFeesRow from './PoolFeesRow'

const PageWrapper = styled(AutoColumn)`
  padding: 68px 8px 0;
  max-width: 870px;
  width: 100%;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    max-width: 800px;
  `};

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    max-width: 500px;
  `};

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 48px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding-top: 20px;
  }
`

const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.textSecondary};
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  `};
`

const MainContentWrapper = styled.main`
  position: relative;
  background-color: ${({ theme }) => theme.backgroundModule};
  padding: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.01), 0 4px 8px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.04),
    0 24px 32px rgba(0, 0, 0, 0.01);
  overflow: hidden;

  ${gradientBorderSecondary}
`

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  max-width: 300px;
  min-height: 25vh;
`

const IconStyle = css`
  width: 48px;
  height: 48px;
  margin-bottom: 0.5rem;
`

const InboxIcon = styled(Inbox)`
  ${IconStyle}
`

export default function FeesStats() {
  const { chainId } = useAccount()
  const theme = useTheme()

  const { positions, loading } = useAllAdminPositions()

  const poolGroups = useMemo(() => {
    if (!positions) return []
    const grouped = groupPositionsByPool(positions)
    return Array.from(grouped.entries())
      .filter(([, poolPositions]) => poolPositions.some((p) => !p.liquidity?.isZero()))
      .sort(([a], [b]) => a.localeCompare(b))
  }, [positions])

  if (!isSupportedChain(chainId)) {
    return (
      <PageWrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow padding="0">
              <ThemedText.LargeHeader>
                <Trans>Fees Stats</Trans>
              </ThemedText.LargeHeader>
            </TitleRow>
            <MainContentWrapper>
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <Trans>Your connected network is unsupported.</Trans>
                </ThemedText.DeprecatedBody>
              </ErrorContainer>
            </MainContentWrapper>
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <AutoColumn gap="lg" justify="center">
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <TitleRow padding="0">
            <ThemedText.LargeHeader>
              <Trans>Fees Stats</Trans>
            </ThemedText.LargeHeader>
          </TitleRow>

          <MainContentWrapper>
            {loading ? (
              <LoadingRows>
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
              </LoadingRows>
            ) : poolGroups.length > 0 ? (
              poolGroups.map(([poolKey, poolPositions]) => <PoolFeesRow key={poolKey} positions={poolPositions} />)
            ) : (
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <InboxIcon strokeWidth={1} style={{ marginTop: '2em' }} />
                  <div>
                    <Trans>No liquidity positions found across admin wallets.</Trans>
                  </div>
                </ThemedText.DeprecatedBody>
              </ErrorContainer>
            )}
          </MainContentWrapper>
        </AutoColumn>
      </AutoColumn>
    </PageWrapper>
  )
}
